// Builds the print sheet (#printSheet): the main plan page, a grouped legend
// with trimmed detail tables, per-Fläche detail crops, reference-image sheets,
// and optional extra pages each showing a single category. Uses window.print().
import { getState, markerCategoryNums, markerDisplayNums } from "./state";
import { markerCats, markerCatById, shapeCatById, flaecheCats } from "./catalog";
import type { Marker, Shape } from "./types";

const esc = (s: string): string =>
  (s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

interface PrintOptions {
  includeLegend: boolean;
  includeDetails: boolean;
  includeRefImages: boolean;
  showNumbers: boolean;
  /** filter keys: "marker:<catId>" | "tensator" | "shape:<catId>" */
  extraMaps: string[];
}

function markerLabel(m: Marker, catNum: number): string {
  const cat = markerCatById(m.category);
  return m.label || `${cat.label} ${catNum}`;
}

let sheetEl: HTMLElement;
let menuEl: HTMLElement;

export function initPrint(el: HTMLElement, btnGo: HTMLButtonElement, menu: HTMLElement): void {
  sheetEl = el;
  menuEl = menu;
  btnGo.addEventListener("click", () => {
    const checked = (id: string, dflt = true) =>
      menu.querySelector<HTMLInputElement>(`#${id}`)?.checked ?? dflt;
    const extraMaps = [...menu.querySelectorAll<HTMLInputElement>('#pExtraMaps input:checked')].map(
      (i) => i.value
    );
    buildPrintSheet({
      // Legende, Detailansichten und Referenzbilder gehören immer zum Ausdruck —
      // nur die zusätzlichen Einzelkategorie-Karten und die Nummern sind Optionen.
      includeLegend: true,
      includeDetails: true,
      includeRefImages: true,
      showNumbers: checked("pOptNumbers", false),
      extraMaps,
    });
    menu.classList.remove("open");
    // Browsers that print their own page-title header/footer use document.title —
    // swap in the project name for the print, then restore it.
    const prevTitle = document.title;
    document.title = getState().project.name || prevTitle;
    const restoreTitle = () => {
      document.title = prevTitle;
      window.removeEventListener("afterprint", restoreTitle);
    };
    window.addEventListener("afterprint", restoreTitle);
    setTimeout(() => window.print(), 60);
  });
}

/** Rebuild the "Zusätzliche Karten" checkbox list from the current catalog. */
export function refreshPrintExtraOptions(): void {
  const host = menuEl?.querySelector<HTMLElement>("#pExtraMaps");
  if (!host) return;
  const { markers, shapes } = getState();
  const rows: Array<{ value: string; label: string }> = [];
  for (const c of markerCats()) {
    if (markers.some((m) => m.category === c.id)) rows.push({ value: `marker:${c.id}`, label: c.label });
  }
  if (shapes.some((z) => z.kind === "line")) rows.push({ value: "tensator", label: "Tensator-Absperrungen" });
  for (const c of flaecheCats()) {
    if (shapes.some((z) => (z.kind === "area" || z.kind === "object") && z.category === c.id)) {
      rows.push({ value: `shape:${c.id}`, label: c.label });
    }
  }
  host.replaceChildren();
  if (!rows.length) {
    host.innerHTML = '<div class="tip" style="margin:2px 0 0;">Noch nichts platziert.</div>';
    return;
  }
  for (const r of rows) {
    const l = document.createElement("label");
    l.className = "check-row";
    l.innerHTML = `<input type="checkbox" value="${esc(r.value)}"> ${esc(r.label)}`;
    host.appendChild(l);
  }
}

/* ------------------------------ map SVG ------------------------------ */

type MarkerFilter = (m: Marker) => boolean;
type ShapeFilter = (z: Shape) => boolean;

function markerGlyph(m: Marker, displayNum: number, catNum: number, showNumber: boolean): string {
  const cat = markerCatById(m.category);
  const g =
    cat.shape === "rect"
      ? `<rect x="-18" y="-18" width="36" height="36" rx="5" fill="${cat.color}" stroke="#fff" stroke-width="3"/>`
      : `<circle r="18" fill="${cat.color}" stroke="#fff" stroke-width="3"/>`;
  const num = showNumber
    ? `<text y="1" font-size="14" text-anchor="middle" dominant-baseline="central" fill="#fff" font-family="ui-monospace, SF Mono, Menlo, monospace" font-weight="700">${displayNum}</text>`
    : "";
  const label = `<text class="ps-marker-label" data-cx="${m.x}" data-cy="${m.y}" data-r="21" x="${m.x}" y="${m.y - 30}" text-anchor="middle">${esc(markerLabel(m, catNum))}</text>`;
  return `<g transform="translate(${m.x},${m.y})">${g}${num}</g>${label}`;
}

/* --------------------- marker-label collision avoidance --------------------- */

const SVGNS = "http://www.w3.org/2000/svg";

interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

function intersects(a: Rect, b: Rect): boolean {
  return a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0;
}

/**
 * A leader line + arrowhead from a marker's dot out toward its label, in the
 * label's direction (`angle`). Always the same on-page length regardless of
 * how close the label itself ended up — a connector clamped to the actual
 * gap to the label text is so short at the (common) nearest candidate that
 * it reads as invisible once printed, so length is fixed instead.
 */
function buildLeader(cx: number, cy: number, angle: number, dotR: number): SVGGElement {
  const color = "#1d1d1f";
  const ux = Math.cos(angle);
  const uy = Math.sin(angle);
  const tipR = dotR + 3;
  const tailR = tipR + 22;
  const tipX = cx + tipR * ux;
  const tipY = cy + tipR * uy;
  const tailX = cx + tailR * ux;
  const tailY = cy + tailR * uy;
  const arrowLen = 9;
  const arrowHalf = 4.5;
  const baseX = tipX - ux * arrowLen;
  const baseY = tipY - uy * arrowLen;
  const perpX = -uy;
  const perpY = ux;

  const g = document.createElementNS(SVGNS, "g");
  g.setAttribute("class", "ps-marker-leader");
  const line = document.createElementNS(SVGNS, "line");
  line.setAttribute("x1", String(tailX));
  line.setAttribute("y1", String(tailY));
  line.setAttribute("x2", String(baseX));
  line.setAttribute("y2", String(baseY));
  line.setAttribute("stroke", color);
  line.setAttribute("stroke-width", "2.2");
  line.setAttribute("stroke-linecap", "round");
  g.appendChild(line);
  const tri = document.createElementNS(SVGNS, "polygon");
  tri.setAttribute(
    "points",
    `${tipX},${tipY} ${baseX + perpX * arrowHalf},${baseY + perpY * arrowHalf} ${baseX - perpX * arrowHalf},${baseY - perpY * arrowHalf}`
  );
  tri.setAttribute("fill", color);
  g.appendChild(tri);
  return g;
}

/**
 * Greedily nudges each marker's print label around its dot (8 directions,
 * 3 radii) until it clears every other dot and every already-placed label,
 * then connects it back to the dot with a small arrow. Falls back to the
 * default "above" spot if nothing is free. Needs real layout (not
 * display:none) for getBBox() to return meaningful sizes, so callers must
 * make `root` visible first.
 */
function layoutMarkerLabels(root: ParentNode): void {
  const ANGLES = [-90, -45, -135, 0, 180, 45, 135, 90].map((d) => (d * Math.PI) / 180);
  const RADII = [46, 66, 90];
  for (const svg of root.querySelectorAll("svg")) {
    const labels = [...svg.querySelectorAll<SVGTextElement>(".ps-marker-label")];
    const placed: Rect[] = labels.map((t) => {
      const cx = Number(t.dataset.cx);
      const cy = Number(t.dataset.cy);
      const r = Number(t.dataset.r);
      return { x0: cx - r, y0: cy - r, x1: cx + r, y1: cy + r };
    });
    for (const t of labels) {
      const cx = Number(t.dataset.cx);
      const cy = Number(t.dataset.cy);
      const dotR = Number(t.dataset.r);
      let chosen: Rect | null = null;
      let chosenAngle = -Math.PI / 2;
      outer: for (const radius of RADII) {
        for (const angle of ANGLES) {
          const x = cx + radius * Math.cos(angle);
          const y = cy + radius * Math.sin(angle);
          t.setAttribute("x", String(x));
          t.setAttribute("y", String(y));
          const b = t.getBBox();
          const rect: Rect = { x0: b.x - 3, y0: b.y - 2, x1: b.x + b.width + 3, y1: b.y + b.height + 2 };
          if (!placed.some((p) => intersects(rect, p))) {
            chosen = rect;
            chosenAngle = angle;
            break outer;
          }
        }
      }
      if (!chosen) {
        // Nothing free — fall back to the default "above" spot.
        chosenAngle = -Math.PI / 2;
        t.setAttribute("x", String(cx));
        t.setAttribute("y", String(cy - (RADII[0] ?? 46)));
        const b = t.getBBox();
        chosen = { x0: b.x - 3, y0: b.y - 2, x1: b.x + b.width + 3, y1: b.y + b.height + 2 };
      }
      placed.push(chosen);

      const leader = buildLeader(cx, cy, chosenAngle, dotR);
      svg.insertBefore(leader, t);
    }
  }
}

function shapeSvg(z: Shape): string {
  const cat = shapeCatById(z.kind, z.category);
  const pts = z.points.map((p) => `${p.x},${p.y}`).join(" ");
  if (z.kind === "line") {
    const tag = z.closed ? "polygon" : "polyline";
    const posts = z.points
      .map((p) => `<circle cx="${p.x}" cy="${p.y}" r="14" fill="#fff" stroke="${cat.color}" stroke-width="5"/>`)
      .join("");
    return `<${tag} points="${pts}" fill="none" stroke="${cat.color}" stroke-width="11" stroke-linecap="round" stroke-linejoin="round"/>${posts}`;
  }
  return `<polygon points="${pts}" fill="${cat.color}" fill-opacity="0.32" stroke="${cat.color}" stroke-width="5"/>`;
}

function mapSvg(fm?: MarkerFilter, fs?: ShapeFilter, showNumbers = false): string {
  const { markers, shapes } = getState();
  const nums = markerDisplayNums();
  const catNums = markerCategoryNums();
  const parts: string[] = [];
  for (const z of shapes) {
    if (fs && !fs(z)) continue;
    parts.push(shapeSvg(z));
  }
  for (const m of markers) {
    if (fm && !fm(m)) continue;
    parts.push(markerGlyph(m, nums.get(m.id) ?? m.num, catNums.get(m.id) ?? 1, showNumbers));
  }
  return parts.join("");
}

function mapPage(title: string, sub: string, svgInner: string, forceBreak: boolean): string {
  const { project } = getState();
  return `<section class="ps-page${forceBreak ? " ps-forcebreak" : ""}">
    <div class="ps-head">
      <div><h1>${esc(project.name)}</h1><div class="sub">${esc(sub)}</div></div>
      <div class="meta">${esc(title)}</div>
    </div>
    <div class="ps-map">
      <img src="${project.planUrl ?? ""}" alt="">
      <svg viewBox="0 0 ${project.imgWidth} ${project.imgHeight}" preserveAspectRatio="xMidYMid meet">${svgInner}</svg>
    </div>
  </section>`;
}

/* ------------------------------ legend ------------------------------ */

function legendBlock(): string {
  const { markers, shapes } = getState();
  const markerRows = markerCats()
    .filter((c) => markers.some((m) => m.category === c.id))
    .map((c) => {
      const n = markers.filter((m) => m.category === c.id).length;
      const img = c.imageUrl ? `<img class="ps-legend-img" src="${c.imageUrl}" alt="">` : "";
      return `<div class="ps-legend-row"><span class="dot ${c.shape === "rect" ? "sq" : ""}" style="background:${c.color}"></span>${img}<span>${esc(c.label)} (${n})</span></div>`;
    })
    .join("");

  const flaecheRows = flaecheCats()
    .filter((c) => shapes.some((z) => (z.kind === "area" || z.kind === "object") && z.category === c.id))
    .map(
      (c) => `<div class="ps-legend-row"><span class="dot sq" style="background:${c.color}"></span>${esc(c.label)}</div>`
    )
    .join("");
  const hasTensator = shapes.some((z) => z.kind === "line");

  const dash = '<div class="ps-legend-row">–</div>';
  return `<div class="ps-legend">
    <h2>Objekte</h2>${markerRows || dash}
    <h2 style="margin-top:10px;">Flächen</h2>${flaecheRows || dash}
    <h2 style="margin-top:10px;">Tensator-Absperrungen</h2>${hasTensator ? '<div class="ps-legend-row"><span class="dot sq" style="background:#b23b2e"></span>Personenleitsystem</div>' : dash}
  </div>`;
}

function notesBlock(): string {
  const { markers, shapes } = getState();
  const nums = markerDisplayNums();

  const noteRows = markers
    .filter((m) => m.note)
    .map((m) => {
      const cat = markerCatById(m.category);
      return `<tr><td>${nums.get(m.id) ?? m.num}</td><td>${esc(m.label || cat.label)}</td><td>${esc(m.note)}</td></tr>`;
    })
    .join("");
  const objNotes = noteRows
    ? `<h2>Objektnotizen</h2><table><tr><th>Nr.</th><th>Bezeichnung</th><th>Notiz</th></tr>${noteRows}</table>`
    : "";

  // Flächen — dauerhaft, daher ohne Zeitraum/Status.
  const flRows = shapes
    .filter((z) => z.kind === "area" || z.kind === "object")
    .map((z) => {
      const cat = shapeCatById(z.kind, z.category);
      return `<tr><td>${esc(z.name || cat.label)}</td><td>${esc(cat.label)}</td><td>${esc(z.note)}</td></tr>`;
    })
    .join("");
  const flTable = flRows
    ? `<h2 style="margin-top:10px;">Flächen im Detail</h2><table><tr><th>Name</th><th>Kategorie</th><th>Notiz</th></tr>${flRows}</table>`
    : "";

  // Tensatoren — nur Einträge mit eigener Bezeichnung oder Notiz.
  const tsRows = shapes
    .filter((z) => z.kind === "line" && (z.name || z.note))
    .map((z) => `<tr><td>${esc(z.name || "—")}</td><td>${esc(z.note)}</td></tr>`)
    .join("");
  const tsTable = tsRows
    ? `<h2 style="margin-top:10px;">Tensator-Absperrungen im Detail</h2><table><tr><th>Bezeichnung</th><th>Notiz</th></tr>${tsRows}</table>`
    : "";

  return `<div class="ps-notes">${objNotes}${flTable}${tsTable}</div>`;
}

/* -------------------------- Fläche detail crops -------------------------- */

function detailCrops(): string {
  const { project, shapes } = getState();
  const flaechen = shapes.filter((z) => z.kind === "area" || z.kind === "object");
  if (!flaechen.length || !project.planUrl) return "";

  const cards = flaechen
    .map((z) => {
      const xs = z.points.map((p) => p.x);
      const ys = z.points.map((p) => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const w = maxX - minX || 1;
      const h = maxY - minY || 1;
      const pad = Math.max(w, h) * 0.4 + 60;
      const vx = minX - pad;
      const vy = minY - pad;
      const vw = w + 2 * pad;
      const vh = h + 2 * pad;
      const cat = shapeCatById(z.kind, z.category);
      const pts = z.points.map((p) => `${p.x},${p.y}`).join(" ");
      const sw = Math.max(3, Math.max(vw, vh) / 140);
      return `<figure class="ps-detail">
        <svg viewBox="${vx} ${vy} ${vw} ${vh}" preserveAspectRatio="xMidYMid slice">
          <image href="${project.planUrl}" x="0" y="0" width="${project.imgWidth}" height="${project.imgHeight}"/>
          <polygon points="${pts}" fill="${cat.color}" fill-opacity="0.22" stroke="${cat.color}" stroke-width="${sw}"/>
        </svg>
        <figcaption>${esc(z.name || cat.label)}<span>${esc(cat.label)}</span></figcaption>
      </figure>`;
    })
    .join("");
  return `<section class="ps-details ps-forcebreak"><h2>Flächen-Detailansichten</h2><div class="ps-detail-grid">${cards}</div></section>`;
}

/* --------------------------- reference images --------------------------- */

interface RefCard {
  url: string;
  caption: string;
  sub: string;
}

function collectRefCards(fm?: MarkerFilter, fs?: ShapeFilter): RefCard[] {
  const { markers, shapes } = getState();
  const nums = markerDisplayNums();
  const cards: RefCard[] = [];

  for (const z of shapes) {
    if (fs && !fs(z)) continue;
    const urls = z.imageUrls ?? [];
    if (!urls.length) continue;
    const cat = shapeCatById(z.kind, z.category);
    const name = z.name || cat.label;
    urls.forEach((url, i) => {
      cards.push({ url, caption: name + (urls.length > 1 ? ` (${i + 1})` : ""), sub: cat.label });
    });
  }

  for (const m of markers) {
    if (fm && !fm(m)) continue;
    const urls = m.imageUrls ?? [];
    if (!urls.length) continue;
    const cat = markerCatById(m.category);
    const name = `#${nums.get(m.id) ?? m.num} ${m.label || cat.label}`;
    urls.forEach((url, i) => {
      cards.push({ url, caption: name + (urls.length > 1 ? ` (${i + 1})` : ""), sub: cat.label });
    });
  }

  return cards;
}

const REFIMG_PER_PAGE = 9;

/** Reference-image pages (3×3 grid, full-resolution/uncropped), for the
 * given filter — one dedicated page per 9 photos, each forced onto its own
 * page so it always lands right after the plan it belongs to. */
function referenceImagePages(fm?: MarkerFilter, fs?: ShapeFilter): string {
  const cards = collectRefCards(fm, fs);
  if (!cards.length) return "";

  const totalPages = Math.ceil(cards.length / REFIMG_PER_PAGE);
  const pages: string[] = [];
  for (let i = 0; i < cards.length; i += REFIMG_PER_PAGE) {
    const chunk = cards.slice(i, i + REFIMG_PER_PAGE);
    const pageNum = i / REFIMG_PER_PAGE + 1;
    const heading = totalPages > 1 ? `Referenzbilder (${pageNum}/${totalPages})` : "Referenzbilder";
    const figs = chunk
      .map(
        (c) => `<figure class="ps-refimg">
          <img src="${c.url}" alt="">
          <figcaption>${esc(c.caption)}<span>${esc(c.sub)}</span></figcaption>
        </figure>`
      )
      .join("");
    pages.push(
      `<section class="ps-refimgs ps-forcebreak"><h2>${heading}</h2><div class="ps-refimg-grid">${figs}</div></section>`
    );
  }
  return pages.join("");
}

/* ------------------------------- assemble ------------------------------- */

function extraMapPage(key: string, sub: string, showNumbers: boolean, includeRefImages: boolean): string {
  let fm: MarkerFilter | undefined;
  let fs: ShapeFilter | undefined;
  let page: string;

  if (key === "tensator") {
    fs = (z) => z.kind === "line";
    page = mapPage("Nur: Tensator-Absperrungen", sub, mapSvg(() => false, fs), true);
  } else if (key.startsWith("marker:")) {
    const id = key.slice(7);
    fm = (m) => m.category === id;
    page = mapPage(`Nur: ${markerCatById(id).label}`, sub, mapSvg(fm, () => false, showNumbers), true);
  } else if (key.startsWith("shape:")) {
    const id = key.slice(6);
    fs = (z) => (z.kind === "area" || z.kind === "object") && z.category === id;
    page = mapPage(`Nur: ${shapeCatById("area", id).label}`, sub, mapSvg(() => false, fs), true);
  } else {
    return "";
  }

  const refs = includeRefImages ? referenceImagePages(fm, fs) : "";
  return page + refs;
}

export function buildPrintSheet(opts: PrintOptions): void {
  const { project } = getState();
  const loc = [project.buildingName, project.floorName].filter((x): x is string => !!x).map(esc).join(" · ");
  const sub = loc || "Grundriss";
  const headMeta = project.scaleRatio ? `Maßstab ${esc(project.scaleRatio)}` : "Grundriss";

  const main = mapPage(headMeta, sub, mapSvg(undefined, undefined, opts.showNumbers), false);
  const legend = opts.includeLegend
    ? `<section class="ps-bottom">${legendBlock()}${notesBlock()}</section>`
    : "";
  const details = opts.includeDetails ? detailCrops() : "";
  // Reference images for the main plan follow right after it (before any
  // extra single-category maps, which bring their own filtered photos).
  const refs = opts.includeRefImages ? referenceImagePages() : "";
  const extras = opts.extraMaps
    .map((k) => extraMapPage(k, sub, opts.showNumbers, opts.includeRefImages))
    .join("");

  sheetEl.innerHTML = main + legend + details + refs + extras;

  // getBBox() needs the sheet actually rendered (not display:none) to return
  // real sizes in every browser — show it off-screen just for the layout pass.
  const prevCss = sheetEl.style.cssText;
  sheetEl.style.cssText = "display:block; position:fixed; left:-99999px; top:0;";
  layoutMarkerLabels(sheetEl);
  sheetEl.style.cssText = prevCss;
}
